<template>
<!-- eslint-disable max-len -->
<span>
  <transition enter-active-class="animated slideInDown" leave-active-class="animated fadeOut">
    <div class="report-container" id="report-container">
      <div :class="`report ${pdf ? 'pdf' : ''}`" ref="report" id="report">
        <div :class="`report-header ${pdf ? 'pdf' : ''}`">
          <!-- logo -->
          <img :src="resolveAssetPath(activeEnvironment.reportLogo || activeEnvironment.creditsLogo || activeEnvironment.titleLogo, 'ui/customer')" :class="`logo ${pdf ? 'pdf' : ''}`" ref="logo">
          <!-- Title -->
          <h1 class="report-title">{{activeEnvironment.reportTitle || game.reportTitle || (`${game.title} Leadership Report`)}}</h1>
        </div>

        <!-- Student/Course/Prof -->
        <div class="overflowbox labelbox secondarybox" style="font-size: 1.65em;">
          <div class="no-wrap right-align">
            Student
          </div>
          <div class="no-wrap right-align">
            {{username}}
          </div>
        </div>
        <div class="overflowbox labelbox primarybox">
          <div class="no-wrap right-align">
            Course
          </div>
          <div class="no-wrap right-align">
            {{course}}
          </div>
        </div>
        <div class="overflowbox labelbox secondarybox">
          <div class="no-wrap right-align">
            Date
          </div>
          <div class="no-wrap right-align">
            {{date}}
          </div>
        </div>

        <!-- Score/Metrics -->
        <div class="overflowbox gradebox primarybox">
          <div
            :class="`width-safari-only score-container ${pdf ? 'pdf' : ''}`"
          >
            <div :class="`score-label ${pdf ? 'pdf' : ''}`">
              STRATEGIC THINKING SCORE:
            </div>
            <div :class="`score-value ${pdf ? 'pdf' : ''}`">
              {{ strategicThinkingScore }}
            </div>
          </div>
          <!-- Score/Metrics -->
          <div :class="`metrics offset-safari-only ${pdf ? 'pdf' : ''}`">
            <metrics-navbar no-scroll labelsBelow :pdf="pdf"/>
          </div>
          <div :class="`metrics invisible-safari-only ${pdf ? 'pdf' : ''}`">
            <metrics-navbar no-scroll labelsBelow :pdf="pdf"/>
          </div>
        </div>

        <div class="report-flex">
          <div class="report-chart">
            <radar
              class="hero-graph"
              :stats="stats"
              :polycolor="activeEnvironment.primaryColor"
              :radar="radar"
              :gridlineCount="9"
              gridlineColor="black"
              axesColor="black"
              :fillColor="activeEnvironment.secondaryColorLightened"
              :halfwayFillColor="activeEnvironment.secondaryColorLightened"
              :borderWidth="10"
              borderColor="black"
              extraBorder
              :labelSize="45"
              labelColor="black"
              :startingAngleRadians="2.08"
              :maxValue="maxRadarValue"
            ></radar>
          </div>
          <div class="report-blurb">
            <h2 class="title">Leadership Style</h2>
            <div class="separator secondarybox"/>
            <p class="content">
              The choices you made reflect {{maxStatWithArticle}} leadership style.
              Leaders flex between different styles based on the situations and challenges they face. One leadership style is not always better or worse than another. Use this report to learn more about your preferred leadership style and consider why it may be your default. Take the time to explore the other styles listed in the report, and how they may or may not work with your current (or future) leadership challenges and why.
            </p>
          </div>
        </div>
        <div class="report-separator overflowbox secondarybox"/>
        <div :class="`report-info ${pdf ? 'pdf' : ''}`">
          <p class="title">Authentic</p>
          <p class="content">
            Authentic leadership is characterized by high moral and ethical standards and conduct. Such leaders exhibit a greater sense of self-awareness in terms of how others are impacted by their leadership. Furthermore, they exhibit transparent relationships with others by openly exchanging ideas and information. They are balanced leaders, meaning they tend to suspend judgment on people and decisions until all relevant data is available.
          </p>
          <p class="title">Transformational</p>
          <p class="content">
            Transformational leadership is characterized by the embodiment of high standards that are associated with one’s beliefs and core values. Such leaders wish to instill these same standards in others. Transformational leaders serve as role models and inspire performance beyond expectations. Such leaders challenge the way others think about problems. Additionally, they help others develop their full leadership potential.
          </p>
          <p class="title">Transactional</p>
          <p class="content">
            Constructive transactional leadership is characterized by a focus on clear expectations, goals, and standards. Such leaders provide feedback on progress and prioritize meeting obligations and commitments. Corrective transactional leadership is characterized by a focus on setting clear standards to monitor performance and intervention to correct deviations from those standards. Such leaders establish precise protocols in order to sustain performance levels by identifying and correcting mistakes.
          </p>
          <p class="title">Directive</p>
          <p class="content">
            Directive leadership is characterized by exerting control over situations and giving orders to others on what is expected in terms of performance. Directive leadership makes it clear who is in charge.
          </p>
          <p class="title">Participative</p>
          <p class="content">
            Participative leadership is characterized by working side-by-side with others and sharing in the decision-making process to come up with the best solutions.
          </p>
          <p class="title">Passive</p>
          <p class="content">
            Passive leadership is characterized by avoidance of conflict and a desire to avoid making decisions. While not taking action may be described as “empowering others,” it usually means an abdication of the responsibility to lead. Passive leaders generally let problems work themselves out.
          </p>
        </div>
        <!-- footer -->
        <div
          v-if="pdf && (activeEnvironment.reportFooter || activeEnvironment.reportFooterLogo)"
          style="
            background-color: black;
            color: white;
            padding: 4px 50px;
            width: 100%;
            min-height: 36px;
            display: flex;
            flex-flow: row nowrap;
            justify-content: space-between;
            align-items: flex-start;
          "
        >
          <!-- footer message (e.g. displaying ©) -->
          <div v-html="activeEnvironment.reportFooter"></div>
          <!-- footer logo -->
          <img
            v-if="activeEnvironment.reportFooterLogo"
            :src="resolveAssetPath(activeEnvironment.reportFooterLogo, 'ui/customer')"
            :class="`footer-logo ${pdf ? 'pdf' : ''}`"
            ref="footerLogo"
          >
        </div>
      </div>
    </div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <div v-if="showDownloading" class="download-message">Downloading!</div>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <button v-if="showNextButton"
      class="btn download-button"
      @click.prevent="onClickDownload"
    >Download</button>
  </transition>

  <transition enter-active-class="animated fadeIn" leave-active-class="animated fadeOut">
    <bottom-bar v-if="showNextButton" :next="showNarratorNotification ? onComplete : undefined" :tabindex="3" ref="bottomBar" :noBar="true">
      <span slot="button">
        <span v-html="(narrative && narrative.nextButtonText) ? narrative.nextButtonText : ((game && game.nextButtonDefaultText) ? game.nextButtonDefaultText : 'Next')"></span>
      </span>
    </bottom-bar>
  </transition>
</span>
</template>

<script>
/* eslint-disable max-len */
import Vue from 'vue';
import { mapActions, mapGetters } from 'vuex';
import JsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';

import MetricsNavbar from '../ui/MetricsNavbar';
import Narrator from '../ui/Narrator';
import NarratorNotification from '../ui/NarratorNotification';
import BottomBar from '../ui/BottomBar';
import Radar from '../ui/Radar';

export default {
  // $emit 'input' on complete
  components: {
    MetricsNavbar,
    Narrator,
    NarratorNotification,
    BottomBar,
    Radar
  },
  data() {
    return {
      showNextButton: false,
      showDownloading: false,
      showNarrator: false,
      showNarratorNotification: false,
      radar: {
        size: '370',
        viewbox: '1300',
        radius: '340'
      },
      maxRadarValue: 1,
      // stats are in this order for the chart, but the metrics should be alphabetical
      // Authentic, Directive, Participative, Passive, Transactional, Transformational
      // Followed by Strategic Thinking at index 6...
      // TODO: we could really use a better system for this,
      // like a mapping field on the narrative object in the authoring tool or something
      stats: [
        {
          name: 'Transformational',
          value: 0,
        },
        {
          name: 'Transactional',
          value: 0,
        },
        {
          name: 'Directive',
          value: 0,
        },
        {
          name: 'Participative',
          value: 0,
        },
        {
          name: 'Passive',
          value: 0,
        },
        {
          name: 'Authentic',
          value: 0,
        }
      ],
      strategicThinkingScore: 0,
      customerMetric: 0,
      employeeMetric: 0,
      shareholderMetric: 0,
      revenue: 0,
      pdf: false
    };
  },
  computed: {
    ...mapGetters([
      'activeEnvironment',
      'activeClass',
      'characters',
      'tooltip',
      'narrative',
      'lastNarrative',
      'nextNarrative',
      'narrativeNavbarText',
      'narrativeText',
      'group',
      'me',
      'metrics',
      'game',
      'dateActiveGameCompleted',
      'challenge'
    ]),
    username() {
      const {
        firstName = '',
        lastName = '',
        email = ''
      } = this.me || {};
      return firstName || lastName ? `${firstName} ${lastName}` : email;
    },
    course() {
      return (this.activeClass != null && this.activeClass.courseName != null)
        ? this.activeClass.courseName
        : ((this.activeEnvironment != null) ? this.activeEnvironment.course : '');
    },
    professor() {
      return (this.activeClass != null && this.activeClass.professorName != null)
        ? this.activeClass.professorName
        : ((this.activeEnvironment != null) ? this.activeEnvironment.professor : '');
    },
    date() {
      return (this.game.carryOverMetricsFromActive && this.dateActiveGameCompleted != null)
        ? new Date(this.dateActiveGameCompleted).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : ((this.challenge && this.challenge.completed)
          ? new Date(this.challenge.completed).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
          : ((this.group && this.group.updated)
            ? new Date(this.group.updated).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
            : new Date(Date.now()).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })));
    },
    reportFileName() {
      return (this.activeClass != null && this.activeClass.studentReportFileName != null)
        ? this.activeClass.studentReportFileName
        : (this.activeEnvironment.reportTitle || this.game.reportTitle || (`${this.game.title} Leadership Report`));
    },
    maxStat() {
      let maxStat = { name: '', value: -1 };
      for (let i = 0; i < this.stats.length; ++i) {
        if (this.stats[i].value > maxStat.value) {
          maxStat = this.stats[i];
        }
      }
      return maxStat.name;
    },
    maxStatWithArticle() {
      if (this.maxStat.startsWith('A')
        || this.maxStat.startsWith('E')
        || this.maxStat.startsWith('I')
        || this.maxStat.startsWith('O')
        || this.maxStat.startsWith('U')) {
        return `an ${this.maxStat.toUpperCase()}`;
      }
      return `a ${this.maxStat.toUpperCase()}`;
    }
  },
  // async created() {
  //   await this.loadPageData();
  //   this.setStats();
  // },
  methods: {
    ...mapActions([
      'stopSounds',
      'getMyGroup'
    ]),
    async show() {
      this.showNarrator = true;
      await Vue.nextTick();
      this.stopSounds();
      await new Promise(resolve => setTimeout(resolve, 1250));
      this.showNarratorNotification = true;
      await new Promise(resolve => setTimeout(resolve, 1250));
      await Vue.nextTick();
    },
    async reset() {
      await Vue.nextTick();
      await this.show();
    },
    navigate() {
      this.$refs.bottomBar.navigate();
    },
    async onComplete() {
      if (this.tooltip && this.tooltip.preventStateProgressionWhileOpen) {
        window.dispatchEvent(new Event('click'));
        return;
      }
      this.$emit('input');
    },
    async loadPageData() {
      try {
        const { _id: groupId } = this.group;
        await this.getMyGroup({ groupId });
      } catch (err) {
        if (await this.$router.handleGameError(err)) return;
        this.$router.showGameErrorCritical();
      }
    },
    setStats() {
      // TODO: the following order-dependent coupling is not ideal, but it currently seems
      //       to be the best way to do this given the way the data is currently organized.
      //       An ID-based coupling seems similarly dicey and a name-based coupling is not
      //       currently possible.

      // group metrics
      this.customerMetric =
        this.group.metrics[0].value
          / (this.group.metrics[0].max != null ? this.group.metrics[0].max / 100 : 1);
      this.employeeMetric =
        this.group.metrics[1].value
          / (this.group.metrics[1].max != null ? this.group.metrics[1].max / 100 : 1);
      this.shareholderMetric =
        this.group.metrics[2].value
          / (this.group.metrics[2].max != null ? this.group.metrics[2].max / 100 : 1);

      this.revenue = this.group.cash;

      // this is a little hard to read. basically it just says:
      // is there a seventh individual metric? if so, treat it
      // as the strategic thinking score. If the metric has the
      // max defined, compute the percentage score as an integer;
      // otherwise use the raw value. and if there's no seventh
      // metric just use 0.
      this.strategicThinkingScore =
        (this.group.players[0].metrics != null
          && this.group.players[0].metrics.length > 6
          && this.group.players[0].metrics[6] != null)
          ? Math.round((this.group.players[0].metrics[6].value
            / (this.group.players[0].metrics[6].max != null
              ? this.group.players[0].metrics[6].max / 100 : 1))) : 0;

      // first 6 individual metrics should be the leadership stats;
      // order is defined by the default initialization of this.stats above
      const leadershipStats = this.group.players[0].metrics.slice(0, 6);
      // console.log(leadershipStats);
      const mLength = Math.min(leadershipStats.length, this.stats.length);
      for (let i = 0; i < mLength; ++i) {
        const leadershipStat = leadershipStats[this.leadershipStatIndexMapping(i)];
        this.stats[i].value = leadershipStat.value == null ? 0 : leadershipStat.value;
        this.maxRadarValue =
          Math.max(
            this.maxRadarValue,
            this.stats[i].value,
            leadershipStat.max == null ? 0 : leadershipStat.max
          );
      }
    },
    leadershipStatIndexMapping(statIndex) {
      // leadership stat order from game (alphabetical):
      // Authentic, Directive, Participative, Passive, Transactional, Transformational
      // stat order for chart (statIndex):
      // Transformational, Transactional, Directive, Participative, Passive, Authentic
      switch (statIndex) {
        case 5: return 0; // Authentic
        case 4: return 3; // Passive
        case 3: return 2; // Participative
        case 2: return 1; // Directive
        case 1: return 4; // Transactional
        case 0: return 5; // Transformational
        default: return -1;
      }
    },
    onBeforePrint(mql) {
      if (mql instanceof Event) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      } else if (mql.matches) {
        this.pdf = true;
        setTimeout(() => {
          this.pdf = false;
        }, 250);
      }
    },
    async onClickDownload() {
      if (this.showDownloading) return;
      this.showDownloading = true;
      // PDF download works in all browsers except pre-chromium Edge
      // (fails at domtoimage.toPng, and no replacement seems viable, see above)
      // fix for Edge (for now): make the page printable, and open the print dialog automatically
      // (in response to domtoimage.toPng or any other part of the autodownload throwing any error)
      // The user can print to PDF or actually print. Bonus is you can print in other browsers too.
      // Edge requires the user to manually set the Scale in the print dialog to 50% for it to fit.
      // Note that this is generally no longer a problem with the new chromium-based Edge.
      this.pdf = true;
      await Vue.nextTick();
      await new Promise((resolve, reject) => {
        if (this.$refs.footerLogo && this.$refs.footerLogo.complete) { // wait for logo to load
          resolve();
        } else if (this.$refs.footerLogo) {
          this.$refs.footerLogo.addEventListener('load', resolve);
          this.$refs.footerLogo.addEventListener('error', reject);
        } else {
          console.log('No footerLogo ref found');
          resolve();
        }
      });
      await domtoimage.toPng(this.$refs.report, { cacheBust: true })
        .then((dataUrl) => {
          const doc = new JsPDF('p', 'pt', 'a4');
          doc.addImage(dataUrl, 'PNG', 30, 44.4, 600, 712);

          // TODO: if no autodownload, no soft-lock (explained below), so soft-lock fix commented out
          doc.save(`${this.reportFileName}.pdf`);
          // Before we download the PDF, we have to consider the possibility that the
          // user's settings are set to view the PDF in the browser first when downloading a PDF
          // (instead of actually downloading it). This can be true in many modern browsers.
          // This is a problem because if it opens in the same page, it destroys the Vue app state.
          // This ends up soft-locking the player such that they can't get past the report page.
          // This problem came to my attention when a Firefox update changed the PDF download behavior
          // to default to viewing it in browser (on the same page) when you initiate a download.
          // To fix this while minimally impacting anyone whose browser actually just downloads stuff
          // when a download is initiated, instead first open a new tab, then artificially initiate
          // the download with a bogus DOM hyperlink element that targets the bloburl output by JsPDF,
          // which either downloads the file or loads the blob viewer. If it doesn't load the
          // blob viewer, we autoclose the tab, leaving you back on the report page. If it does load
          // the blob viewer, the user is left in the blob viewer, where they can either:
          // - close it, returning to the original report tab without downloading, where they can
          //   hit print or refresh to save off the report another way, or continue on with the game.
          // - download/print it (which presents a system dialog, and does carry over the expected
          //   default name) (and then close it to return back to the game).
          // const win = window.open(`${window.location.origin}/downloadplayerreport/${encodeURIComponent(this.reportFileName)}/${encodeURIComponent(doc.output('bloburl', { filename: `${this.reportFileName}.pdf` }))}`, '_blank');
          // if (win == null) {
          //   doc.save(`${this.reportFileName}.pdf`);
          // }

          this.pdf = false;
          this.showDownloading = false;
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error('Failed to autodownload report PDF!', error);
          try {
            this.pdf = true;
            window.print();
            this.pdf = false;
            this.showDownloading = false;
          } catch (e) {
            // eslint-disable-next-line no-console
            console.error('Failed to open print dialog!', e);
            this.pdf = false;
            this.showDownloading = false;
          }
        });
    },
  },
  watch: {
    async narrative(newValue) {
      if (newValue) await this.reset();
    }
  },
  async mounted() {
    this.setStats();

    // polyfill for safari but works for others as well
    const mediaQueryList = (window.matchMedia != null) ? window.matchMedia('print') : null;
    if (mediaQueryList != null && mediaQueryList.addListener != null) {
      mediaQueryList.removeListener(this.onBeforePrint);
      mediaQueryList.addListener(this.onBeforePrint);
    } else {
      // what you'd normally use, if you weren't needing to support Safari:
      window.removeEventListener('beforeprint', this.onBeforePrint);
      window.addEventListener('beforeprint', this.onBeforePrint);
    }

    await this.show();

    await new Promise((resolve, reject) => {
      if (this.$refs.logo.complete) { // wait for logo to load
        resolve();
      } else {
        this.$refs.logo.addEventListener('load', resolve);
        this.$refs.logo.addEventListener('error', reject);
      }
    });

    // uncomment to enable autodownload (but re-add soft-lock fix above if you do)
    // await this.onClickDownload();

    this.showNextButton = true;
  }
};
</script>

<style lang="scss" scoped>
@media print {
  body, body * {
    visibility: hidden !important;
    overflow: visible !important;
  }
  #report-container, #report-container * {
    visibility: visible !important;
    overflow: visible !important;
  }
  #report-container {
    left: 8.75% !important;
    top: 0 !important;
    width: 82.5% !important;
    overflow: visible !important;
    z-index: 10 !important;
  }

  @supports (-moz-appearance:none) { // a hack for detecting FF... its printing is also awful
    .report {
      top: -30% !important;
      transform: scale(0.8);
    }
  }
}

.download-button {
  position: absolute;
  bottom: 210px;
  right: 0;
  width: 350px;
  font-size: 28px;
  line-height: 55px;
  margin-right: 45px;
  z-index: 100 !important;
  transition: opacity 0.5s ease-in-out;
}

hr {
  border-top: 1px solid #e9ecef;
}
.report {
  background-color: white;

  // color: var(--primaryColor);
  color: black;

  // border: solid;
  // border-width: 3.5px;
  // border-color: var(--secondaryBorderColor);

  // border: 20px solid;
  // border-image-slice: 1;
  // border-image-source: linear-gradient(to bottom left, rgb(32, 237, 252), rgb(16, 89, 102));
}
.report.pdf {
  border: solid;
  border-width: 3.5px;
  border-color: var(--primaryColor);
}
.report-container {
  position: absolute;
  width: 1150px;
  height: 850px;
  top: 20px;
  left: 30px;
  z-index: 1;
  // overflow: auto;
}

.report-flex {
  margin-top: 0;
  width: 99.97%;
  height: 255px;
  display: flex;
  justify-content: space-between;
  background-color: transparent;
  color: black;
  position: relative;
  //box-shadow: inset 0px 0px 20px 0px rgba(219,212,219,1);
}
.report-chart {
  margin-top: -5%;
  fill: black !important;
}
.report-blurb {
  padding-top: 85px;
  padding-left: 5%;
  .title {
    text-transform: uppercase;
    font-family: Georgia, 'Times New Roman', Times, serif;
    font-size: 1.5em;
    margin-bottom: 0;
  }
  .separator {
    height: 6px;
    width: calc(100% + 21px);
  }
  .content {
    line-height: 123%;
    text-align: justify;
    padding-right: 30px;
  }
}

.report-separator {
  height: 6px;
}

.report-info {
  overflow-y: scroll; // auto doesn't work on macOS Safari for some reason
  color: black;
  font-size: 1.3em;
  height: 255px;
  padding: 15px 25px;
  line-height: 110%;
  .title {
    text-transform: uppercase;
    font-weight: bolder;
    font-size: 120%;
    text-align: left;
    margin-bottom: 0;
  }
  .content {
    text-align: justify;
    margin-bottom: 30px;
  }
}
.report-info.pdf {
  overflow-y: visible !important;
  height: 765px !important;
}

.report-header {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  height: 100px;
}
.report-header.pdf {
  flex-direction: column !important;
  height: 165px !important;
  padding: 8px !important;
}
.logo {
  object-fit: contain;
  height: 90%;
  max-width: 40%;
  margin: 10px 0px 10px 16px;
  z-index: 10;
}
.logo.pdf {
  height: calc(55% - 16px) !important;
  margin-bottom: 0 !important;
}
.footer-logo {
  object-fit: contain;
  max-height: 30px;
  max-width: 30%;
  margin-bottom: 15px;
  z-index: 10;
}
.report-title {
  font-size: 1.65em;
  font-family: Georgia, 'Times New Roman', Times, serif; // Verdana, Arial, Helvetica, sans-serif;
  color: black;
  //border-bottom: solid 8px var(--secondaryColor);
  margin: 10px;
  white-space: pre-wrap;
  text-transform: uppercase;
  text-align: center;
  max-width: 60%;
  max-height: calc(45% - 16px);
}

.accent-color {
  background-color: #D9CDA8;
  color: black;
}

.overflowbox {
  margin-left: -21px;
  width: calc(100% + 42px);
  box-shadow: 4px 6px 12px #7e7e7e88;
}

.labelbox {
  border-radius: 1px;
  padding: 0 80px;
  height: 40px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-size: 1.5em;
}

.no-wrap {
  white-space: nowrap;
}

.right-align {
  justify-self: flex-end;
  text-align: right;
  width: fit-content;
  padding: 0;
  margin: 0;
}

.gradebox {
  padding: 0 80px;
  margin-top: 40px;
  height: 80px;
  border-radius: 1px;
  // box-shadow: 4px 6px 12px #88888888;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  font-size: 1.7em;
}

.score-container {
  font-size: 85%;
  white-space: nowrap;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  width: 42%;
}
.score-container.pdf {
  width: 39% !important;
}

.score-label {
  min-width: 50%;
  white-space: nowrap !important;
}
.score-label.pdf {
  font-size: 90%;
  // width: 75%;
}

.score-separator {
  background-color: rgba(255, 255, 255, 0.637);
  height: 2px;
  width: 528px;
}

.score-value {
  font-weight: bold;
  font-size: 200%;
  // width: 25%;
  text-align: center;
  margin-left: 20px;
}
.score-value.pdf {
  // width: 20%;
  // text-align: center;
  // margin-left: 20% !important;
  text-align: right !important;
  position: relative;
  right: -50px;
}

.bluebox {
  background-color: rgb(12, 174, 185);
  color: white;
}

.orangebox {
  background-color: rgb(201, 114, 57);
  color: white;
}

.primarybox {
  background-color: var(--primaryColor);
  color: white;

  color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

.secondarybox {
  background-color: var(--secondaryColor);
  color: white;

  color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

.metrics {
  position: relative;
  top: 24px; // needs 48px when running dev, but 24px in build... for some reason.
  height: 160px;
  width: 50%;
  color: black;
}

.offset-safari-only {
  display: none;
}
@media not all and (min-resolution:.001dpcm) {
  @supports (-webkit-appearance:none) {
    .offset-safari-only.pdf {
      top: -48px !important;
      left: -420px !important;
      display: unset !important;
      border: none !important;
    }
    .invisible-safari-only.pdf {
      visibility: hidden !important;
      left: -150px !important;
    }
    .width-safari-only.pdf {
      width: 40% !important;
    }
  }
}

.slideInDown {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.19, 1, 0.22, 1); // out expo
}
.fadeOutUp {
  animation-duration: 1s;
  animation-timing-function: cubic-bezier(0.55, 0.055, 0.675, 0.19); // in cubic
}
.narrator-notification {
  bottom: 205px;
}

.download-message {
  position: absolute;
  top: 50%;
  left: 25%;
  z-index: 1000;
  border-radius: 15px;
  border: 3px solid black;
  background-color: var(--primaryColor);
  color: white;
  font-size: 30pt;
  padding: 20px;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/components/game/narrative/NarrativeCorpReportHSG.vue